import {
  CandidateProfile,
  CandidateListing,
  CandidateCalendar,
  SiteListing,
  SiteProfile,
  DailyBooking,
  BookingCalendar,
  WeeklyCalendar,
  UserManagement,
  Document,
  CostAnalysis,
  Infringement,
  CandidatePerformance,
  SystemSetup,
  PayRateListing,
  GeneratePayrate,
  FinanceHoliday,
  VerifyBooking,
  Sts,
  ClientManagement,
  Reports
} from "../../routes/modulesPath";
import constants from "@constants/constants";

import roles from "../../constants/roles";

const {
  PAGE_ROUTES: {
    DEFAULT_ROUTE,
    CANDIDATE_LISTING_ROUTE,
    CANDIDATE_CALENDAR_ROUTE,
    SITE_LISTING_ROUTE,
    DAILY_BOOKING_ROUTE,
    MONTHLY_CALENDAR_ROUTE,
    WEEKLY_CALENDAR_ROUTE,
    SITE_PROFILE_ROUTE,
    CANDIDATE_PROFILE_ROUTE,
    USER_MANAGEMENT_ROUTE,
    GENERATE_PAYRATE_ROUTE,
    HOLIDAY_ROUTE,
    DOCUMENTS_ROUTE,
    COST_ANALYSIS_ROUTE,
    CANDIDATE_PERFORMANCE_ROUTE,
    INFRINGEMENT_ROUTE,
    SYSTEM_SETUP_ROUTE,
    MANAGE_PAY_RATES_ROUTE,
    VERIFY_BOOKING_ROUTE,
    STS_ROUTE,
    CLIENT_MANAGEMENT_ROUTE,
    REPORTS_ROUTE
  }
} = constants;

const {
  AGENCY_ADMIN,
  AGENCY_USER,
  CLIENT_USER,
  CLIENT_TIME_SHEET,
  CLIENT_FINANCE_USER,
  FINANCE_USER,
  SYSTEM_ADMIN
} = roles;

const CLIENT_ROLES = [CLIENT_USER, CLIENT_TIME_SHEET, CLIENT_FINANCE_USER];
const AGENCY_ROLES = [AGENCY_ADMIN, AGENCY_USER];
const BOOKING_ROLES = [...AGENCY_ROLES, ...CLIENT_ROLES];

/**
 * Single source of truth for protected path access and sub-header tab visibility.
 * `roles` controls both ProtectedRoute access and which tabs appear for the active platform role.
 * Omit `tab` for nested/detail routes that should not appear in the sub-header.
 */
export const appRouteConfig = [
  {
    path: DEFAULT_ROUTE,
    roles: [CLIENT_USER, AGENCY_ADMIN, AGENCY_USER, FINANCE_USER, SYSTEM_ADMIN],
    component: DailyBooking
  },
  {
    path: GENERATE_PAYRATE_ROUTE,
    roles: [FINANCE_USER],
    component: GeneratePayrate,
    tab: { key: "GENERATE_PAYRATE", label: "Generate Document", order: 10 }
  },
  {
    path: HOLIDAY_ROUTE,
    roles: [FINANCE_USER],
    component: FinanceHoliday,
    tab: { key: "HOLIDAY", label: "Holidays", order: 9 }
  },
  {
    path: CANDIDATE_LISTING_ROUTE,
    roles: AGENCY_ROLES,
    component: CandidateListing,
    tab: { key: "CANDIDATE_LISTING", label: "Candidate", order: 1 }
  },
  {
    path: CANDIDATE_CALENDAR_ROUTE,
    roles: AGENCY_ROLES,
    component: CandidateCalendar
  },
  {
    path: MONTHLY_CALENDAR_ROUTE,
    roles: BOOKING_ROLES,
    component: BookingCalendar
  },
  {
    path: DAILY_BOOKING_ROUTE,
    roles: BOOKING_ROLES,
    component: DailyBooking,
    tab: { key: "BOOKING_MANAGEMENT", label: "Bookings", order: 4 }
  },
  {
    path: WEEKLY_CALENDAR_ROUTE,
    roles: BOOKING_ROLES,
    component: WeeklyCalendar
  },
  {
    path: CANDIDATE_PROFILE_ROUTE,
    roles: BOOKING_ROLES,
    component: CandidateProfile
  },
  {
    path: SITE_LISTING_ROUTE,
    roles: AGENCY_ROLES,
    component: SiteListing,
    tab: { key: "SITE_LISTING", label: "Site", order: 3 }
  },
  {
    path: SITE_PROFILE_ROUTE,
    roles: AGENCY_ROLES,
    component: SiteProfile
  },
  {
    path: USER_MANAGEMENT_ROUTE,
    roles: [AGENCY_ADMIN, SYSTEM_ADMIN],
    component: UserManagement,
    tab: { key: "USER_MANAGEMENT", label: "User", order: 5 }
  },
  {
    path: DOCUMENTS_ROUTE,
    roles: [FINANCE_USER],
    component: Document,
    tab: { key: "DOCUMENT", label: "Documents", order: 11 }
  },
  {
    path: COST_ANALYSIS_ROUTE,
    roles: CLIENT_ROLES,
    component: CostAnalysis,
    tab: { key: "CLIENT_REPORTS", label: "Reports", order: 14 }
  },
  {
    path: CANDIDATE_PERFORMANCE_ROUTE,
    roles: CLIENT_ROLES,
    component: CandidatePerformance,
    tab: { key: "CANDIDATE_PERFORMANCE", label: "Candidate Performance", order: 16 }
  },
  {
    path: INFRINGEMENT_ROUTE,
    roles: CLIENT_ROLES,
    component: Infringement,
    tab: { key: "INFRINGEMENT", label: "Infringements", order: 15 }
  },
  {
    path: SYSTEM_SETUP_ROUTE,
    roles: [AGENCY_ADMIN, SYSTEM_ADMIN],
    component: SystemSetup,
    tab: { key: "BOOKING_SETUP", label: "System", order: 6 }
  },
  {
    path: MANAGE_PAY_RATES_ROUTE,
    roles: [FINANCE_USER],
    component: PayRateListing,
    tab: { key: "MANAGE_PAY_RATES", label: "Pay and Charge Rates", order: 12 }
  },
  {
    path: VERIFY_BOOKING_ROUTE,
    roles: [FINANCE_USER],
    component: VerifyBooking,
    tab: { key: "VERIFY_BOOKING", label: "Verify Booking", order: 8 }
  },
  {
    path: STS_ROUTE,
    roles: AGENCY_ROLES,
    component: Sts,
    tab: { key: "STS", label: "STS", order: 7 }
  },
  {
    path: CLIENT_MANAGEMENT_ROUTE,
    roles: [AGENCY_ADMIN],
    component: ClientManagement,
    tab: { key: "CLIENT_MANAGEMENT", label: "Client", order: 2 }
  },
  {
    path: REPORTS_ROUTE,
    roles: [...AGENCY_ROLES, FINANCE_USER],
    component: Reports,
    tab: { key: "REPORTS", label: "Reports", order: 13 }
  }
];

export const getTabsForRole = role =>
  appRouteConfig
    .filter(route => route.tab && route.roles.includes(role))
    .sort((a, b) => a.tab.order - b.tab.order)
    .map(({ path, tab }) => ({
      label: tab.label,
      key: tab.key,
      route: path
    }));

export default appRouteConfig;
